import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit2, Plus, Trash2, Search } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import SqlImportButton from "../../components/SqlImportButton";
import { api } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const emptyForm = {
  tipo: "SINGULAR",
  nome: "",
  nif: "",
  telefone: "",
  email: "",
  endereco: "",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Clientes = () => {
  const user = useSelector((state) => state.user.user);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchingNif, setSearchingNif] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/clientes");
      const allClientes = response.data?.data || response.data || [];
      const companyClientes = allClientes.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setClientes(companyClientes);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar clientes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchClientes();
    }
  }, [user?.empresa_id]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleConsultNif = async () => {
    if (!form.nif || form.nif.trim() === "") {
      await notify("Insira um NIF para pesquisar.", "warning");
      return;
    }

    try {
      setSearchingNif(true);
      const response = await api.get(`/nif/${form.nif.trim()}`);
      const nifData = response.data?.contribuinte || response.data;

      if (nifData) {
        setForm((current) => ({
          ...current,
          nome: nifData.nome || nifData.designacao || current.nome,
          endereco: nifData.endereco || current.endereco,
          tipo: nifData.tipo || (nifData.designacao ? "COLLECTIVE" : current.tipo),
        }));
        await notify("Dados do NIF encontrados e preenchidos.", "success");
      } else {
        await notify("Nenhum dado encontrado para este NIF.", "info");
      }
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível consultar o NIF.", "error");
    } finally {
      setSearchingNif(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      tipo: item.tipo || "SINGULAR",
      nome: item.nome || "",
      nif: item.nif || "",
      telefone: item.telefone || "",
      email: item.email || "",
      endereco: item.endereco || "",
    });
    showModal("clienteFormModal");
  };

  const payload = () => {
    const data = { ...form };
    if (user?.empresa_id) {
      data.empresa_id = user.empresa_id;
    }
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        await api.post(`/clientes/${editing.id}`, payload());
        await notify("Cliente actualizado com sucesso.", "success");
      } else {
        await api.post("/clientes", payload());
        await notify("Cliente cadastrado com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      closeModal("clienteFormModal");
      fetchClientes();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar o cliente.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir cliente",
      text: `Deseja excluir o cliente ${selected.nome}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/clientes/${selected.id}`);
      await notify("Cliente excluído com sucesso.", "success");
      setSelected(null);
      fetchClientes();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir o cliente.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin" || user?.nivel === "operador";

  return (
    <ResourcePage
      title="Clientes"
      breadcrumb={["Gestão", "Clientes"]}
      actions={(
        <>
          <SqlImportButton endpoint="/importar/clientes" disabled={loading || !canManage} onImported={fetchClientes} />
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#clienteFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Não tem permissões para gerir clientes.</div>}

          <DataTable
            id="clientesDataTable"
            columns={["Tipo", "Nome", "NIF", "Telefone", "Email", "Endereço"]}
            rows={clientes}
            renderRow={(item) => (
              <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                <td><span className={`badge ${item.tipo === "COLLECTIVE" ? "bg-info-subtle text-info" : "bg-primary-subtle text-primary"}`}>{item.tipo}</span></td>
                <td>{item.nome}</td>
                <td>{item.nif || "-"}</td>
                <td>{item.telefone || "-"}</td>
                <td>{item.email || "-"}</td>
                <td>{item.endereco || "-"}</td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="clienteFormModal"
        title={editing ? "Editar cliente" : "Cadastrar cliente"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="clienteForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="clienteForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="tipo">Tipo</label>
              <select className="form-select" id="tipo" name="tipo" value={form.tipo} onChange={handleChange} required>
                <option value="SINGULAR">Singular</option>
                <option value="COLLECTIVE">Colectivo</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="nif">NIF</label>
              <div className="input-group">
                <input className="form-control" id="nif" name="nif" value={form.nif} onChange={handleChange} placeholder="Digite o NIF" />
                <button className="btn btn-outline-secondary d-flex align-items-center" type="button" onClick={handleConsultNif} disabled={searchingNif} title="Consultar NIF">
                  <Search size={16} />
                </button>
              </div>
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label" htmlFor="nome">Nome</label>
              <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="telefone">Telefone</label>
              <input className="form-control" id="telefone" name="telefone" value={form.telefone} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="email">Email</label>
              <input className="form-control" id="email" name="email" type="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label" htmlFor="endereco">Endereço</label>
              <textarea className="form-control" id="endereco" name="endereco" rows="2" value={form.endereco} onChange={handleChange}></textarea>
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Clientes;
